/**
 * src/components/departmentNegj.js — Хэсэг (цех доторх нэгж) сонгох Select.
 *
 * onChange(departmentcode, departmentname)
 *
 * Хэсгийг олох логик:
 *   1. getUserDepartment()-аас level-5 хэсгүүдийг авна (эрхээр шүүгдсэн).
 *   2. ЦЕХ СОНГОГДСОН бол зөвхөн ТҮҮНИЙ хэсгүүдийг үзүүлнэ —
 *      parentid таарах ЭСВЭЛ кодын угтвар таарах (0105 → 010501…).
 *   3. Цех сонгогдоогүй үед л эрхийн хүрээн дэх бүх хэсгийг үзүүлнэ.
 *   4. Хэрэглэгчийн өөрийн negj-ийг зөвхөн ТУХАЙН цехэд хамаарч байвал нэмнэ.
 *
 * ⚠ Өмнө нь 2-р алхам «тохирох хэсэг БАЙВАЛ л шүүнэ» гэсэн байсан тул
 *   parentid таарахгүй үед шүүлт чимээгүй алгасагдаж, сонгосон цехийнх биш
 *   БҮХ цехийн хэсэг жагсаалтад орж ирдэг байв. Мөн 4-р алхам нь өөр цехийн
 *   хэрэглэгчийн хэсгийг албаар нэмдэг байлаа.
 *
 * Props: paranetid (цехийн id) · parentCode (цехийн код — угтвараар тааруулах)
 *        autoSelectOwn=false бол өөрийн хэсгийг автоматаар сонгохгүй.
 *        stage — хяналтын шат ("IV"). Backend-ийн хамрах хүрээг өргөснө.
 *
 * ⚠ `stage` ЗААВАЛ: 4 дүгээр шатанд ӨӨР цех сонгогдож болдог бөгөөд
 *   параметргүй дуудвал тэр цехийн хэсгүүд хэрэглэгчийн хүрээнд
 *   БАЙХГҮЙ тул хэсгийн жагсаалт ХООСОН гардаг. Цехийн сонгогчид
 *   (`departmentTseh.js`) дамжуулсан утгыг энд мөн дамжуулна.
 */

import React, { useEffect, useRef, useState } from "react";
import { useUserContext } from "src/contexts/userContext";
import * as API from "src/api/request";
import { Select } from "antd";
import _ from "lodash";

/**
 * Сонгогдсон ХЭСЭГ нь шинэ ЭЦЭГ (цех/хэлтэс)-т хамаарах эсэх.
 *
 * Кодын бүтэц: цех 2 орон (25) · хэлтэс 4 орон (0105) · хэсэг нь эцгийн
 * УГТВАРТАЙ (25 → 2505, 0105 → 010501).
 *
 * @returns {boolean} false бол хэсгийг цэвэрлэх ёстой.
 */
export function sectionBelongsToParent(value, parentCode) {
  const v = String(value == null ? "" : value).replace(/\D/g, "");
  const p = String(parentCode == null ? "" : parentCode).replace(/\D/g, "");
  if (!v) return true; // хэсэг сонгоогүй — хоосруулах зүйлгүй
  if (!p) return false; // ЭЦЭГ ХООСОН → ямар ч хэсэг хүчингүй
  return v.startsWith(p);
}

const DepartmentNegj = (props) => {
  const { user, isAdmin, checkGroup } = useUserContext();
  // sso_owner / админ — Цех сонгогчтой ИЖИЛ горим: өөрийн хэсгийг
  // автоматаар сонгохгүй, бүх хэсэг нээлттэй («Бүгд» төлөвт үлдэнэ).
  const admin = !!(isAdmin && isAdmin());

  /**
   * «БҮГДИЙГ ХАРНА» — `departmentTseh.js`-ийн `seesAll`-ТАЙ ЯГ ИЖИЛ ДҮРЭМ.
   *
   * ⚠ ЯАГААД (2026-09-28): өмнө нь энд ЗӨВХӨН `admin` (sso_owner) шалгадаг
   *   байсан тул хос сонгогч нэг асуултад ӨӨР хариу өгдөг байв:
   *     ХАБ хэлтсийн мэргэжилтэн (бүлэг 173) /inspection/register нээхэд
   *       Цех  → «Бүгд» (seesAll тул авто-сонголт хийхгүй)
   *       Хэсэг → ӨӨРИЙНХ нь хэсэг автоматаар сонгогдоно
   *   Үр дүнд нь «БН хоосон атал хэсэг дүүрэн» болж, жагсаалт нь тэр
   *   хэсгээр ЧИМЭЭГҮЙ шүүгдэж, ХАБ-ынхан бусад нэгжийн шалгалтыг
   *   огт харахгүй байсан.
   *
   * ⚠ 167 (шат шатны хяналт) нь хасагдана — тэд өөрийн нэгжээрээ ажиллана.
   * ⚠ МАЯГТАД өөрийн хэсэг урьдчилан сонгогдох шаардлагатай бол дуудагч
   *   `autoSelectOwn`-ыг ИЛТ өгнө — тэр нь энэ дүрмээс ДАВУУ (доорх
   *   `wantAuto`-г үз). Цех сонгогч дээрх зохицуулалт ч яг ийм.
   */
  const seesAll =
    admin ||
    !!(checkGroup && checkGroup([173, 174, 240, 380]) && !checkGroup([167]));
  const [list, setList] = useState([]);
  /* ⚠ ЭЦЭГ СОЛИГДОХЫГ хянана (2026-10-05, хэрэглэгч: «аль ч хуудасны БН
     хоосолсон үед дагаж хэсэг хоосрохгүй бна»).

     ЯАГААД КОМПОНЕНТ ДОТОР: сонгогдсон хэсгийн утга нь ХУУДАСНЫ төлөв
     (`value` prop) тул `key`-ээр дахин угсрах нь түүнийг цэвэрлэдэггүй —
     зөвхөн доторх жагсаалтыг л шинэчилнэ. Үр дүнд цехийг «Бүгд» болгосон
     ч хуучин хэсгээр ЧИМЭЭГҮЙ шүүлт үлдэж, хэрэглэгч «бүх цехийг харлаа»
     гэж ойлгоод нэг хэсгийн датаг харж байдаг.

     ⚠ ПРОП НЬ null-аас тодорхой утга болох ҮЕД цэвэрлэхгүй: бүртгэл
     ачаалагдахад (цех+хэсэг зэрэг тавигдана) шинэ утгыг устгах эрсдэлтэй.
     Тиймээс анхны ажиллалтыг (`null`) алгасаад, утга нь шинэ эцгийн
     УГТВАРТАЙ бол хөндөхгүй. */
  const prevParentRef = useRef(null);

  // truncateLength prop — кодыг нь N оронгоор тасдаж дискплэй болон утганд
  // ашиглана (зөвхөн тоог авна, цэг гэх мэт тэмдэгтгүй).
  const truncCode = (raw) => {
    if (!props.truncateLength) return String(raw || "");
    const digits = String(raw || "").replace(/\D/g, "");
    return digits.slice(0, props.truncateLength);
  };

  // Сонгогдсон хэсгийн нэрийг (string) олж эцэгт буцаах
  const emitChange = (code, currentList) => {
    if (!props.onChange) return;
    if (code === undefined || code === null || code === "") {
      props.onChange("", "");
      return;
    }
    const item = _.find(
      currentList || list,
      (x) => String(x.departmentcode) === String(code),
    );
    // truncateLength байгаа бол кодыг тасдсан хэлбэрээр буцаана.
    const outCode = props.truncateLength ? truncCode(code) : code;
    props.onChange(outCode, item ? item.departmentname : "");
  };

  useEffect(() => {
    let mounted = true; // unmount болсны дараа setState хийхээс сэргийлнэ
    API.getUserDepartment(props.stage)
      .then((res) => {
        if (!mounted) return;
        const all = res || [];

        // 1) Эрхийн хүрээн дэх бүх level-5 хэсэг
        let sections = all.filter((a) => a.departmentlevelid === 5);

        // ── ЭЦЭГ СОНГОГДСОН ЭСЭХ ────────────────────────────────────
        // Кодын бүтэц: ЦЕХ 2 орон (25) · ХЭЛТЭС 4 орон (0105) · хэсэг нь
        // тэдгээрийн УГТВАРТАЙ (0105 → 010501, 25 → 2501…). Тиймээс эцэгт
        // харьяалагдахыг parentid ЭСВЭЛ кодын угтвараар тодорхойлно —
        // parentid нь зарим суулгацад хоосон ирдэг тул угтвар найдвартай.
        // ⚠ Дуудагч ихэвчлэн зөвхөн `paranetid` (DepartmentTseh-ийн parentId)
        //   өгдөг. Тэр id-ээр ЭЦГИЙН КОДЫГ энд өөрсдөө олно — учир нь:
        //     • хэсгийн `parentid` нь цех БИШ, завсрын нэгж байж болно
        //       (21 → 2102 → 210201: parentid=2197, цехийн id=6 биш) —
        //       зөвхөн parentid-ээр шүүвэл тэдгээр хэсэг АЛГА болно
        //       («Засвар механик 21-ийн хэсэг бүрэн орж ирэхгүй», 2026-09-22);
        //     • DepartmentTseh нь backend-ээс цех ирээгүй үед user.info-оос
        //       fallback хийж `id = код` өгдөг тул parentid хэзээ ч таарахгүй.
        //   Кодын угтвар нь хоёуланг нь шийднэ.
        let parentCode = String(props.parentCode || "").replace(/\D/g, "");
        if (!parentCode && props.paranetid) {
          const parentItem = all.find(
            (x) => String(x.id) === String(props.paranetid),
          );
          const raw = parentItem
            ? parentItem.departmentcode
            : /^\d+$/.test(String(props.paranetid))
              ? props.paranetid
              : "";
          parentCode = String(raw || "").replace(/\D/g, "");
        }
        const hasParent = !!(props.paranetid || parentCode);

        if (hasParent && parentCode) {
          // ⚠ level-5-ээр хязгаарлахгүй: цехийн доторх ЗАВСРЫН нэгж
          //   (жш 2102 «Механик цех» — level 4) нь сонгогчид гардаггүй
          //   мөртлөө АЖИЛТНЫ бүртгэлд яг тэр кодоор олон хүн тэмдэглэгдсэн
          //   байдаг тул тэднийг сонгох арга байхгүй болдог байв
          //   (2026-09-23, /morningxab оролцогч сонголт).
          /* ⚠ ЗӨВХӨН НЭГ ТҮВШИН ДООШ: эцгийн код + 2 орон.
               цех 21   (2 орон) → 2101, 2102, 2104…      (4 орон)
               хэлтэс 0107 (4)   → 010701, 010703…        (6 орон)
             Гүн нэгжийг (ГРУПП: 253001, 210201 — эцгээс 4 орон урт)
             ХАСНА. Өмнө нь «эцгээс урт бүхэн» гэж авдаг байсан тул
             «Автоматжуулалтын цех 25» дээр 2530-ийн 2 групп, 2535-ын 3
             групп зэрэг нэмэгдэж, 2501 дээр 4 мөр гарч байв
             (хэрэглэгч 2026-09-26-нд мэдэгдсэн).
             ⚠ Групп-ийг хассан ч ХАМРАХ ХҮРЭЭ алдагдахгүй: хэсгийн код
               нь групп-ийн УГТВАР (2530 → 253001) бөгөөд backend нь
               `negj_code`-ыг УГТВАРААР шүүдэг. */
          /* ⚠ ЗӨВХӨН УРТААР шүүж БОЛОХГҮЙ (2026-10-05). «Эцэг + 2 орон»
             гэсэн дүрэм нь ЗМЗ (21) дээр хэсэг дутуу гаргаж байв:

               21      lvl 4  Засвар, механикийн завод
               2101    lvl 5  Захиргаа                    ← хэсэг
               2102    lvl 9  Механик цех                 ← ДЭД ЦЕХ
               210201  lvl 5  МЦ-ийн Механикийн засвар…   ← ХЭСЭГ, 6 оронтой
               210202  lvl 5  МЦ-ийн Эрчим хүчний засвар… ← ХЭСЭГ
               2103    lvl 9  Цутгуурын цех               ← ДЭД ЦЕХ
               210301, 210302  lvl 5                      ← ХЭСЭГ

             2102/2103 нь ХЭСЭГ БИШ, өөрсдөө цех. Тэдний доорх жинхэнэ
             хэсэг нь 6 оронтой тул уртаар шүүхэд унаж байв.

             ТҮВШНЭЭР шүүнэ (өгөгдлөөс, кодоор таамаглахгүй):
               lvl 5  = ХЭСЭГ   → ЯМАР Ч гүнд авна
               бусад  = завсрын → ЗӨВХӨН нэг түвшин доош (сонгогдох
                        ёстой: ажилтан яг 2102 кодоор бүртгэгддэг)
             Групп (lvl 7) ба товчоо (lvl 8) нь эцгээс 4 орон урт тул
             хоёр нөхцөлд ч орохгүй — 2026-09-26-ны гомдол хэвээр
             шийдэгдсэн («2501 дээр 4 илүү мөр гарч байв»). */
          const byCode = all.filter((a) => {
            const code = String(a.departmentcode || "").replace(/\D/g, "");
            if (!code.startsWith(parentCode)) return false;
            if (code.length <= parentCode.length) return false;
            if (Number(a.departmentlevelid) === 5) return true;
            return code.length === parentCode.length + 2;
          });
          if (byCode.length > 0) {
            sections = byCode;
          }
        } else if (hasParent) {
          sections = sections.filter((a) => {
            if (
              props.paranetid &&
              String(a.parentid) === String(props.paranetid)
            ) {
              return true;
            }
            if (!parentCode) return false;
            const code = String(a.departmentcode || "").replace(/\D/g, "");
            // Угтвар таарах ба ЗӨВХӨН нэг түвшин доош (эцэг + 2 орон)
            return (
              code.startsWith(parentCode) &&
              code.length === parentCode.length + 2
            );
          });
          // ⚠ Тохирох хэсэг олдохгүй бол ХООСОН үлдээнэ. Өмнө нь энд бүх
          //   цехийн хэсгийг буцаадаг байсан тул сонгосон цехэд хамаарахгүй
          //   хэсгүүд жагсаалтад орж ирдэг байв.
        } else if (sections.length === 0) {
          // Цех сонгогдоогүй үед л level-5 олдохгүй бол доод нэгжээр нөхнө
          sections = all.filter((a) => Number(a.departmentlevelid) > 4);
        }

        // Хэрэглэгчийн өөрийн negj-ийг нэмнэ — ГЭХДЭЭ зөвхөн сонгосон цехэд
        // хамаарч байвал. Эс бөгөөс өөр цех сонгоход өөрийн хэсэг нь
        // жагсаалтад үлдэж, буруу шүүлт үүсгэнэ.
        const myNegjCode = user?.info?.negj_code;
        const myBelongs =
          !hasParent ||
          (parentCode &&
            String(myNegjCode || "")
              .replace(/\D/g, "")
              .startsWith(parentCode));
        if (
          myNegjCode &&
          myBelongs &&
          !sections.some(
            (x) => String(x.departmentcode) === String(myNegjCode),
          )
        ) {
          sections = [
            {
              departmentcode: myNegjCode,
              departmentname:
                user?.info?.negj_namemn || String(myNegjCode),
              id: myNegjCode,
              departmentlevelid: 5,
            },
            ...sections,
          ];
        }

        let ordered = _.orderBy(sections, ["departmentcode"]);
        // truncateLength байвал кодыг тасдаж давтсан утгуудыг арилгана
        // (жишээ нь "250501", "250502" → нэг л "2505" сонголт).
        // Нэрийн хувьд тухайн 4 оронт код-той тохирох эцгийг (level 4)
        // `all` массиваас олж ашиглана — level-5 хүүхдийн нэр (хяналт/засвар
        // гэх мэт sub-нэр) биш зөв БҮТЭЦ-ийн нэр харагдана.
        if (props.truncateLength) {
          const seen = new Set();
          ordered = ordered.reduce((acc, item) => {
            const t = truncCode(item.departmentcode);
            if (!t || seen.has(t)) return acc;
            seen.add(t);
            // Эцэг (4-оронт код-той department-ийг all-аас хайх — аль ч level)
            const parent = (all || []).find(
              (x) => truncCode(x.departmentcode) === t,
            );
            acc.push({
              ...item,
              departmentcode: t,
              departmentname:
                parent?.departmentname || item.departmentname || t,
              id: t,
              originalCode: item.departmentcode,
            });
            return acc;
          }, []);
        }
        setList(ordered);

        // ── ЭЦЭГ СОЛИГДВОЛ ТААРАХГҮЙ ХЭСГИЙГ ЦЭВЭРЛЭНЭ ──────────────
        const parentKey = `${props.paranetid || ""}|${props.parentCode || ""}`;
        const firstRun = prevParentRef.current === null;
        const parentChanged = !firstRun && prevParentRef.current !== parentKey;
        prevParentRef.current = parentKey;
        if (parentChanged && props.value) {
          if (!sectionBelongsToParent(props.value, parentCode)) {
            emitChange("");
            return; // цэвэрлэсний дараа авто-сонголт хийхгүй
          }
        }

        // Хэрэглэгчийн negj-ийг анхдагчаар сонгох.
        // autoSelectOwn === false бол автомат сонголт хийхгүй (жш: 2-р шатны
        // хяналтын хэрэглэгч лист дээр хэсгээ бүхэлд нь харах ёстой).
        //
        // ⚠ Админ/ХАБЭА хэрэглэгчид анхдагчаар «Бүгд» үлддэг — ЖАГСААЛТАД
        //   зөв. Харин МАЯГТАД (шинэ наряд ажил) энэ нь дутагдал: тэд ажил
        //   үүсгэх бүрд хэсгээ гараар сонгох шаардлагатай болдог. Тиймээс
        //   дуудагч `autoSelectOwn`-ыг ИЛТ өгвөл админ дээр ч ажиллана.
        /* ⚠ ЦЕХИЙГ ХООСОЛСНЫ ДАРАА АВТО-СОНГОЛТ ХИЙХГҮЙ (2026-10-05):
           хэрэглэгч цехийг «Бүгд» болгох нь «бүх нэгжийг харъя» гэсэн
           ИЛЭРХИЙ хүсэл. Тэр үед өөрийн хэсгийг дахин сонговол хэсэг
           хоосрохгүй болж, жагсаалт нь нэг хэсгээр чимээгүй шүүгдсэн
           хэвээр үлдэнэ. Анхны ачаалалт (`firstRun`) ба цех СОНГОСОН
           тохиолдолд авто-сонголт хэвээр — маягт дээр хэсэг урьдчилан
           бөглөгдөх нь хэрэгтэй. */
        const autoBlocked = parentChanged && !parentCode;
        const wantAuto =
          !autoBlocked &&
          (props.autoSelectOwn === true ||
            (!seesAll && props.autoSelectOwn !== false));
        if (wantAuto && ordered.length > 0 && !props.value && myNegjCode) {
          const found = _.find(
            ordered,
            (d) => String(d.departmentcode) === String(myNegjCode),
          );
          if (found) emitChange(found.departmentcode, ordered);
        }
      })
      .catch(() => {
        if (mounted) setList([]);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // parentCode ч бас хамаарна — цех солигдоход жагсаалт дахин угсрагдана
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `props.stage` нь хамаарал — шат солигдоход хамрах хүрээ өөрчлөгдөнө.
  }, [props.paranetid, props.parentCode, props.stage]);

  return (
    <Select
      showSearch
      allowClear
      size={props.size}
      placeholder={
        list.length === 0
          ? "Хэсэг байхгүй"
          : props.placeholder || "Хэсэг сонгох"
      }
      /* ⚠ `nameOnly` үед кодыг ч, нэрийг ч хайна — харагдах текстээс
           код хасагдвал кодоор хайх боломж алга болно. */
      {...(props.nameOnly
        ? {
            filterOption: (input, opt) => {
              const t = String(input || "").toLowerCase();
              const it = _.find(
                list,
                (x) => String(x.departmentcode) === String(opt?.value),
              );
              return (
                String(opt?.value || "").toLowerCase().includes(t) ||
                String(it?.departmentname || "").toLowerCase().includes(t)
              );
            },
          }
        : { optionFilterProp: "children" })}
      className="w-full"
      value={props.value}
      disabled={props.disabled}
      onChange={(code) => emitChange(code)}
    >
      {_.map(list, (item) => (
        <Select.Option
          key={item.id || item.departmentcode}
          value={item.departmentcode}
        >
          {/* ⚠ `nameOnly` — зөвхөн нэр (2026-11-05). Анхдагчаар хуучнаар:
              энэ сонгогчийг олон хуудас хэрэглэдэг. */}
          {props.nameOnly
            ? item.departmentname
            : `${item.departmentcode} | ${item.departmentname}`}
        </Select.Option>
      ))}
    </Select>
  );
};

export default React.memo(DepartmentNegj);
